import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { TestTube, PaperPlaneTilt, CheckCircle, MagnifyingGlass, CircleNotch } from "@phosphor-icons/react";

export function TestEmailPanel({ defaultTemplateId = "" }) {
  const { user } = useAuth();
  const [recipient, setRecipient] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(defaultTemplateId);
  const [mode, setMode] = useState("sample"); // sample | account
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [account, setAccount] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { setRecipient(user?.email || ""); }, [user]);
  useEffect(() => {
    api.get("/connect/email/studio/templates", { params: { status: "active" } })
      .then((r) => { const t = r.data?.templates || []; setTemplates(t); if (!templateId && t[0]) setTemplateId(t[0].id); })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (mode !== "account" || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/accounts", { params: { q: query, limit: 6 } }); setResults(data?.items || []); }
      catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, mode]);

  const send = async () => {
    if (!recipient.trim()) { toast.error("Enter a recipient email"); return; }
    if (mode === "account" && !account) { toast.error("Select an account or switch to sample data"); return; }
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/connect/email/studio/test-send", {
        recipient_email: recipient.trim(), template_id: templateId || null,
        use_sample: mode === "sample", account_id: mode === "account" ? account?.account_id : null,
      });
      setResult(data);
      toast.success("Test email sent — check your inbox.");
    } catch (e) { toast.error(e.response?.data?.detail || "Test email failed"); }
    finally { setSending(false); }
  };

  const Row = ({ k, v }) => (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-[11px] uppercase tracking-wide text-emerald-700/70 font-semibold">{k}</span>
      <span className="text-xs font-medium text-emerald-900 text-right">{v}</span>
    </div>
  );

  return (
    <div className="border border-border rounded-sm p-4 bg-card" data-testid="test-email-panel">
      <div className="flex items-center gap-2 mb-1">
        <TestTube size={18} weight="bold" className="text-primary" />
        <h2 className="font-bold text-sm">Test Email</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Sends a real email through your exact live pipeline (branding, hosted logo, template, sender) — but charges 0 RC and never touches customer history.</p>

      <div className="space-y-3">
        <div>
          <Label className="text-xs">Recipient Email</Label>
          <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="you@company.com" className="rounded-sm mt-1" data-testid="test-recipient" />
        </div>
        <div>
          <Label className="text-xs">Template</Label>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="test-template"><SelectValue placeholder="Choose a template" /></SelectTrigger>
            <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " ★" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Data</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button onClick={() => setMode("sample")} className={`py-1.5 rounded-sm text-xs font-semibold border ${mode === "sample" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid="test-mode-sample">Use sample data</button>
            <button onClick={() => setMode("account")} className={`py-1.5 rounded-sm text-xs font-semibold border ${mode === "account" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid="test-mode-account">Use an account</button>
          </div>
        </div>
        {mode === "account" && (
          <div>
            {account ? (
              <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2 bg-secondary/40">
                <span className="text-sm font-medium truncate">{account.borrower_name} · <span className="mono text-xs text-muted-foreground">{account.loan_id}</span></span>
                <Button variant="ghost" size="sm" className="rounded-sm h-7" onClick={() => setAccount(null)}>Change</Button>
              </div>
            ) : (
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search account…" className="rounded-sm pl-8" data-testid="test-account-search" />
                {results.length > 0 && (
                  <div className="mt-1 border border-border rounded-sm divide-y max-h-40 overflow-y-auto">
                    {results.map((a) => (
                      <button key={a.account_id} onClick={() => { setAccount(a); setResults([]); setQuery(""); }} className="w-full text-left px-3 py-1.5 hover:bg-secondary/60 text-sm">
                        {a.borrower_name} <span className="mono text-[11px] text-muted-foreground">{a.loan_id}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <Button onClick={send} disabled={sending} className="rounded-sm w-full" data-testid="test-send-btn">
          {sending ? <CircleNotch size={15} className="mr-1.5 animate-spin" /> : <PaperPlaneTilt size={15} weight="bold" className="mr-1.5" />}
          {sending ? "Sending…" : "Send Test Email"}
        </Button>

        {result && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3 mt-1" data-testid="test-result">
            <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-sm mb-2"><CheckCircle size={16} weight="fill" /> Test sent to {result.to}</div>
            <Row k="From" v={result.sending_as} />
            <Row k="Mode" v={<span className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" className="text-emerald-600" />{result.mode_label}</span>} />
            <Row k="Template" v={result.template_name} />
            <Row k="Branding" v={`v${result.branding_version ?? "-"}`} />
            <Row k="Template Version" v={result.template_version ? `v${result.template_version}` : "—"} />
            <Row k="RC" v="0 (Test Email)" />
          </div>
        )}
      </div>
    </div>
  );
}
